import { A } from "@solidjs/router";
import { createSignal, Show, Switch, Match } from "solid-js";
import { AplombButton, AplombChip, AplombField } from "~/components";
import { AplombApiClient, type SubmitUrgencyRequest } from "~/lib/api/client";
import "./urgence.css";

type Categorie = SubmitUrgencyRequest["category"];

interface FormState {
  clientEmail: string;
  postalFsa: string;
  categorie: Categorie;
  description: string;
  phone: string;
}

interface Validation {
  clientEmail?: string;
  postalFsa?: string;
  description?: string;
  phone?: string;
}

function validate(form: FormState): Validation {
  const errors: Validation = {};
  if (!form.clientEmail.includes("@")) {
    errors.clientEmail = "Adresse courriel invalide";
  }
  if (form.postalFsa.trim().length !== 3) {
    errors.postalFsa = "Les 3 premiers caractères du code postal (ex : H2X)";
  }
  if (form.description.trim().length < 30) {
    errors.description = "Décrivez le problème en au moins 30 caractères";
  }
  if (form.phone.replace(/\D/g, "").length < 10) {
    errors.phone = "Numéro de téléphone à 10 chiffres requis";
  }
  return errors;
}

const client = new AplombApiClient();

export default function Urgence() {
  const [form, setForm] = createSignal<FormState>({
    clientEmail: "",
    postalFsa: "",
    categorie: "urgency-plumbing",
    description: "",
    phone: "",
  });
  const [errors, setErrors] = createSignal<Validation>({});
  const [status, setStatus] = createSignal<
    | { kind: "idle" }
    | { kind: "submitting" }
    | { kind: "success"; requestId: string; etaMinutes: number; proposalsExpected: number }
    | { kind: "error"; title: string; detail: string }
  >({ kind: "idle" });

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm({ ...form(), [key]: value });
  };

  const onSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    const v = validate(form());
    setErrors(v);
    if (Object.keys(v).length > 0) return;

    setStatus({ kind: "submitting" });
    const result = await client.submitUrgencyRequest({
      client_email: form().clientEmail.trim(),
      postal_code_prefix: form().postalFsa.trim().toUpperCase(),
      category: form().categorie,
      description: form().description.trim(),
      phone: form().phone.trim(),
    });

    if (result.ok) {
      setStatus({
        kind: "success",
        requestId: result.value.request_id,
        etaMinutes: result.value.eta_minutes,
        proposalsExpected: result.value.proposals_expected,
      });
    } else {
      setStatus({ kind: "error", title: result.title, detail: result.detail });
    }
  };

  return (
    <>
      <header class="a-nav">
        <div class="a-container a-nav__inner">
          <A href="/" class="a-nav__brand">
            <span class="a-nav__brandmark">A|</span>
            <span class="a-nav__brandtext">Aplomb</span>
          </A>
          <A href="/" class="a-nav__back">← Accueil</A>
        </div>
      </header>

      <main class="a-container a-urgency">
        <Switch>
          <Match when={status().kind === "success"}>
            {(() => {
              const s = status() as Extract<ReturnType<typeof status>, { kind: "success" }>;
              return (
                <section class="a-urgency__success">
                  <AplombChip variant="conforming">Demande enregistrée</AplombChip>
                  <h1>On vous rappelle sous {s.etaMinutes} minutes.</h1>
                  <p>
                    Numéro de suivi : <strong>{s.requestId}</strong>
                  </p>
                  <p>
                    Nous relançons {s.proposalsExpected} plombiers vérifiés dans votre zone. Vous
                    recevrez leurs propositions par courriel et téléphone — comparaison honnête, sans
                    pression.
                  </p>
                  <A href="/">
                    <AplombButton variant="secondary">Retour à l'accueil</AplombButton>
                  </A>
                </section>
              );
            })()}
          </Match>

          <Match when={status().kind !== "success"}>
            <section class="a-urgency__form">
              <AplombChip variant="attention" compact>
                Réponse humaine en 30 minutes
              </AplombChip>
              <h1>Signaler une urgence de plomberie</h1>
              <p class="a-urgency__intro">
                Décrivez le problème. Aucune inscription requise. Aucun frais de dossier. Vous ne
                payez rien tant qu'un plombier n'est pas accepté et le travail vérifié.
              </p>

              <form onSubmit={onSubmit} novalidate>
                <fieldset disabled={status().kind === "submitting"}>
                  <AplombField
                    label="Courriel de contact"
                    type="email"
                    required
                    autocomplete="email"
                    value={form().clientEmail}
                    onInput={(e) => update("clientEmail", e.currentTarget.value)}
                    error={errors().clientEmail}
                    helper="Nous vous envoyons les propositions ici."
                  />

                  <AplombField
                    label="Téléphone"
                    type="tel"
                    required
                    autocomplete="tel-national"
                    value={form().phone}
                    onInput={(e) => update("phone", e.currentTarget.value)}
                    error={errors().phone}
                    helper="514 555-0142 — pour rappel rapide."
                  />

                  <AplombField
                    label="Code postal (3 premiers caractères, ex : H2X)"
                    type="postal-fsa"
                    required
                    autocomplete="postal-code"
                    value={form().postalFsa}
                    onInput={(e) => update("postalFsa", e.currentTarget.value.toUpperCase())}
                    error={errors().postalFsa}
                    helper="Sert à trouver les plombiers vérifiés de votre secteur."
                  />

                  <div class="a-field">
                    <label for="cat" class="a-field__label">
                      Nature de l'urgence <span aria-hidden="true">*</span>
                    </label>
                    <select
                      id="cat"
                      class="a-field__input"
                      value={form().categorie}
                      onInput={(e) => update("categorie", e.currentTarget.value as Categorie)}
                    >
                      <option value="urgency-plumbing">Fuite / plomberie générale</option>
                      <option value="urgency-drainage">Drainage / refoulement</option>
                      <option value="urgency-water-heater">Chauffe-eau</option>
                    </select>
                  </div>

                  <div class={`a-field${errors().description ? " a-field--error" : ""}`}>
                    <label for="desc" class="a-field__label">
                      Décrivez la situation <span aria-hidden="true">*</span>
                    </label>
                    <textarea
                      id="desc"
                      class="a-field__input a-field__textarea"
                      rows="5"
                      value={form().description}
                      onInput={(e) => update("description", e.currentTarget.value)}
                      aria-invalid={errors().description ? "true" : undefined}
                    />
                    <Show when={errors().description}>
                      <p class="a-field__error" role="alert">
                        {errors().description}
                      </p>
                    </Show>
                    <Show when={!errors().description}>
                      <p class="a-field__helper">
                        Depuis quand ? Zone touchée ? Avez-vous coupé l'eau ? Photo utile mentionnée.
                      </p>
                    </Show>
                  </div>

                  <Show when={status().kind === "error"}>
                    {(() => {
                      const s = status() as Extract<ReturnType<typeof status>, { kind: "error" }>;
                      return (
                        <div class="a-urgency__error" role="alert">
                          <strong>{s.title}</strong>
                          <p>{s.detail}</p>
                        </div>
                      );
                    })()}
                  </Show>

                  <AplombButton
                    variant="primary"
                    size="large"
                    type="submit"
                    fullWidth
                    loading={status().kind === "submitting"}
                  >
                    Recevoir des propositions de plombiers
                  </AplombButton>

                  <p class="a-urgency__legal">
                    En envoyant, vous acceptez la{" "}
                    <A href="/confidentialite">politique de confidentialité</A>. Aucun démarchage
                    hors de votre demande.
                  </p>
                </fieldset>
              </form>
            </section>
          </Match>
        </Switch>
      </main>
    </>
  );
}
